// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * A door's swing (frontend-u0o.5): which side of its host it opens to, which
 * jamb it hangs on, one leaf or two, and its type name ("Enkeltdør 90").
 *
 * The wire carries them on the opening (ruxd #32, ReUseX #131):
 * `swing_side` (`a` | `b` | `unknown`), `hinge` (`start` | `end` |
 * `unknown`), `operation` (one leaf `single_swing`, two `double_swing`, …)
 * and `type_name` ("" = none). Rows written before that (by an earlier
 * mock-ruxd) kept them in `metadata` (`swing_side`, `hinge`, `leaves`,
 * `type_name`); those are read as a fallback, never written.
 *
 *  - side `a` is the host's left of start → end (its +normal), `b` its right;
 *  - hinge `start` is the jamb at `s_start`.
 *
 * Revit's "hand" is how the hinge reads from the swing side, facing the
 * wall: V (venstre) or H (højre). Pure: no React, no DOM.
 */

import type { OpeningOperation, SolidOpening } from '../api/types';

export type SwingSide = 'a' | 'b';
export type Hinge = 'start' | 'end';
export type Hand = 'V' | 'H';

export interface OpeningSwing {
  side: SwingSide;
  hinge: Hinge;
  leaves: 1 | 2;
  typeName: string | null;
  /** Stored (not the default a detected door gets). */
  known: boolean;
}

export const DEFAULT_SWING: OpeningSwing = { side: 'a', hinge: 'start', leaves: 1, typeName: null, known: false };

type Row = Pick<SolidOpening, 'metadata'> & Partial<Pick<SolidOpening, 'swing_side' | 'hinge' | 'operation' | 'type_name'>>;

const metaOf = (o: Row): Record<string, unknown> =>
  typeof o.metadata === 'object' && o.metadata !== null && !Array.isArray(o.metadata) ? (o.metadata as Record<string, unknown>) : {};

/** The swing of opening `o`: the wire's fields, else (an older row) its metadata, else the default. */
export function readSwing(o: Row): OpeningSwing {
  const meta = metaOf(o);
  const side = o.swing_side === 'a' || o.swing_side === 'b' ? o.swing_side : meta.swing_side === 'a' || meta.swing_side === 'b' ? meta.swing_side : null;
  const hinge = o.hinge === 'start' || o.hinge === 'end' ? o.hinge : meta.hinge === 'start' || meta.hinge === 'end' ? meta.hinge : null;
  const op = o.operation;
  const leaves = op === 'double_swing' ? 2 : op === 'single_swing' ? 1 : meta.leaves === 2 ? 2 : 1;
  const typeName = typeof o.type_name === 'string' && o.type_name !== '' ? o.type_name : typeof meta.type_name === 'string' && meta.type_name !== '' ? meta.type_name : null;
  return { side: side ?? 'a', hinge: hinge ?? 'start', leaves, typeName, known: side !== null || hinge !== null };
}

/** A door's leaves as the wire's `operation`. */
export const leavesOperation = (leaves: 1 | 2): OpeningOperation => (leaves === 2 ? 'double_swing' : 'single_swing');

/** The swing as the fields a create's record carries (a window or an opening: only its type name). */
export function swingRecord(swing: Omit<OpeningSwing, 'known'>, door: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = door ? { swing_side: swing.side, hinge: swing.hinge, operation: leavesOperation(swing.leaves) } : {};
  if (swing.typeName) out.type_name = swing.typeName;
  return out;
}

/** The `set` that gives door `o` the swing `patch` (the rest as it reads now). */
export function swingPatch(o: Row, patch: Partial<Omit<OpeningSwing, 'known' | 'typeName'>>): Record<string, unknown> {
  const next = { ...readSwing(o), ...patch };
  return { swing_side: next.side, hinge: next.hinge, operation: leavesOperation(next.leaves) };
}

/** How hinge `hinge` reads from side `side`, facing the wall: from side a the host's start lies to the right. */
export function hingeLabel(side: SwingSide, hinge: Hinge): Hand {
  return (side === 'a') === (hinge === 'start') ? 'H' : 'V';
}

/** The hinge jamb that reads as `hand` from side `side`. */
export function hingeFor(side: SwingSide, hand: Hand): Hinge {
  return (side === 'a') === (hand === 'H') ? 'start' : 'end';
}

export const otherSide = (s: SwingSide): SwingSide => (s === 'a' ? 'b' : 'a');
export const otherHinge = (h: Hinge): Hinge => (h === 'start' ? 'end' : 'start');
